import React from "react";
import {
  CornerUpLeft, CornerUpRight, CornerDownLeft, ArrowUp, ArrowUpRight, ArrowUpLeft,
  RotateCw, Merge, Navigation, LocateFixed, X, Loader2, Search, Play, ShieldCheck, ShieldAlert, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import GoMap from "./GoMap";
import { getGoRoute, placeAutocomplete } from "../../lib/api";
import { haversineM } from "../../lib/polyline";

const RISK = {
  low: { c: "#10B981", bg: "#D1FAE5", label: "Low risk", Icon: ShieldCheck },
  medium: { c: "#F59E0B", bg: "#FEF3C7", label: "Some risk", Icon: ShieldAlert },
  high: { c: "#EF4444", bg: "#FEE2E2", label: "High risk", Icon: ShieldAlert },
};

function maneuverIcon(m) {
  const map = {
    "turn-left": CornerUpLeft, "turn-sharp-left": CornerUpLeft, "turn-slight-left": ArrowUpLeft,
    "turn-right": CornerUpRight, "turn-sharp-right": CornerUpRight, "turn-slight-right": ArrowUpRight,
    "uturn-left": CornerDownLeft, "uturn-right": CornerDownLeft,
    "roundabout-left": RotateCw, "roundabout-right": RotateCw,
    "ramp-left": ArrowUpLeft, "ramp-right": ArrowUpRight, "fork-left": ArrowUpLeft, "fork-right": ArrowUpRight,
    merge: Merge, straight: ArrowUp,
  };
  return map[m] || Navigation;
}

function PlaceField({ value, onChange, placeholder, icon, color, testid, onLocate }) {
  const [sug, setSug] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const timer = React.useRef(null);
  const skip = React.useRef(false);

  const change = (v) => {
    onChange(v);
    if (skip.current) { skip.current = false; return; }
    clearTimeout(timer.current);
    if (v.trim().length < 2) { setSug([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try { const r = await placeAutocomplete(v); setSug(r.suggestions || []); setOpen((r.suggestions || []).length > 0); }
      catch { setSug([]); setOpen(false); }
    }, 250);
  };
  const pick = (d) => { skip.current = true; onChange(d); setOpen(false); setSug([]); };

  return (
    <div className="relative flex items-center gap-2.5 rounded-2xl bg-white/[0.06]px-3.5 py-2.5">
      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: color }} />
      <input
        value={value}
        onChange={(e) => change(e.target.value)}
        onFocus={() => sug.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 160)}
        placeholder={placeholder}
        className="w-full bg-transparent text-[15px] font-medium text-white outline-none placeholder:text-[#67799A]"
        data-testid={testid}
        autoComplete="off"
      />
      {onLocate && (
        <button onClick={onLocate} className="shrink-0 text-[#1E4DB7] active:scale-90" data-testid={`${testid}-locate`}>
          <LocateFixed size={18} strokeWidth={2.3} />
        </button>
      )}
      {open && (
        <ul className="absolute left-0 right-0 top-full z-[20] mt-1 max-h-56 overflow-y-auto rounded-2xl border border-white/10 bg-[#0F1E3A] py-1 shadow-xl" data-testid={`${testid}-suggestions`}>
          {sug.map((s, i) => (
            <li key={s.place_id || i}
              onMouseDown={(e) => { e.preventDefault(); pick(s.description); }}
              className="cursor-pointer px-4 py-2.5 text-sm text-[#C2D0E8] active:bg-white/[0.06]"
              data-testid={`${testid}-suggestion-${i}`}>
              {s.description}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const NavigateScreen = ({ prefillDest, onVerdict }) => {
  const [originText, setOriginText] = React.useState("My location");
  const [originCoords, setOriginCoords] = React.useState(null);
  const [destText, setDestText] = React.useState(prefillDest || "");
  const [data, setData] = React.useState(null);
  const [selIdx, setSelIdx] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [navMode, setNavMode] = React.useState(false);
  const [userPos, setUserPos] = React.useState(null);
  const [stepIdx, setStepIdx] = React.useState(0);
  const [showSteps, setShowSteps] = React.useState(false);
  const watchRef = React.useRef(null);

  React.useEffect(() => {
    if (prefillDest) setDestText(prefillDest);
  }, [prefillDest]);

  React.useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => { const c = [p.coords.latitude, p.coords.longitude]; setOriginCoords(c); setUserPos(c); },
      () => {}, { enableHighAccuracy: true, timeout: 8000 }
    );
  }, []);

  const locateMe = () => {
    if (!navigator.geolocation) return toast.error("Location not available on this device");
    navigator.geolocation.getCurrentPosition(
      (p) => { const c = [p.coords.latitude, p.coords.longitude]; setOriginCoords(c); setUserPos(c); setOriginText("My location"); toast.success("Using your live location"); },
      () => toast.error("Couldn't get your location — turn on GPS"),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const fetchRoute = async () => {
    if (!destText.trim()) return toast.error("Where are you heading?");
    const origin = originText === "My location" && originCoords ? `${originCoords[0]},${originCoords[1]}` : originText.trim();
    if (!origin) return toast.error("Set a starting point");
    setLoading(true);
    try {
      const res = await getGoRoute(origin, destText.trim());
      setData(res);
      setSelIdx(res.recommended_index);
      onVerdict && onVerdict({ ...res.verdict, dest: destText.trim() });
    } catch (e) {
      toast.error("No route found — try a nearby landmark");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const sel = data?.routes?.[selIdx];

  const startNav = () => {
    if (!sel) return;
    setNavMode(true); setStepIdx(0); setShowSteps(false);
    if (navigator.geolocation) {
      watchRef.current = navigator.geolocation.watchPosition(
        (p) => setUserPos([p.coords.latitude, p.coords.longitude]),
        () => {}, { enableHighAccuracy: true, maximumAge: 2000 }
      );
    }
    toast.success("Navigation started — drive safely");
  };

  const stopNav = () => {
    setNavMode(false);
    if (watchRef.current != null && navigator.geolocation) navigator.geolocation.clearWatch(watchRef.current);
    watchRef.current = null;
  };

  React.useEffect(() => () => { if (watchRef.current != null && navigator.geolocation) navigator.geolocation.clearWatch(watchRef.current); }, []);

  // advance the current step as the user approaches each maneuver
  React.useEffect(() => {
    if (!navMode || !sel || !userPos) return;
    const steps = sel.steps || [];
    let i = stepIdx;
    while (i < steps.length - 1 && haversineM(userPos, [steps[i].end_lat, steps[i].end_lng]) < 40) i++;
    if (i !== stepIdx) setStepIdx(i);
  }, [userPos, navMode, sel, stepIdx]);

  const steps = sel?.steps || [];
  const cur = steps[stepIdx];
  const next = steps[stepIdx + 1];
  const distToTurn = navMode && cur && userPos ? haversineM(userPos, [cur.end_lat, cur.end_lng]) : null;
  const CurIcon = cur ? maneuverIcon(cur.maneuver) : Navigation;

  return (
    <div className="relative h-full w-full overflow-hidden bg-slate-100">
      <div className="absolute inset-0">
        <GoMap
          routes={data?.routes || []}
          selectedIdx={selIdx}
          recommendedIdx={data?.recommended_index || 0}
          start={sel?.start}
          end={sel?.end}
          userPos={userPos}
          follow={navMode}
        />
      </div>

      {/* Search card */}
      {!navMode && (
        <div className="go-fade-up absolute left-0 right-0 top-0 z-[900] px-4 pt-[max(env(safe-area-inset-top),14px)]">
          <div className="rounded-[1.75rem] bg-[#0F1E3A]/95 ring-1 ring-white/10 p-3 backdrop-blur-xl" style={{ boxShadow: "0 10px 40px rgba(0,0,0,0.5)" }}>
            <div className="space-y-2">
              <PlaceField value={originText} onChange={setOriginText} placeholder="Starting point" color="#1E4DB7" testid="nav-origin" onLocate={locateMe} />
              <PlaceField value={destText} onChange={setDestText} placeholder="Where to?" color="#EF4444" testid="nav-destination" />
            </div>
            <button
              onClick={fetchRoute}
              disabled={loading}
              className="mt-2.5 flex w-full items-center justify-center gap-2 rounded-full bg-[#1E4DB7] py-3 text-[15px] font-bold text-white transition-transform active:scale-[0.98] disabled:opacity-60"
              data-testid="nav-find-route"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} strokeWidth={2.5} />}
              {loading ? "Finding safest route…" : "Find lowest-risk route"}
            </button>
          </div>
        </div>
      )}

      {/* Live nav header */}
      {navMode && cur && (
        <div className="absolute left-0 right-0 top-0 z-[900] px-4 pt-[max(env(safe-area-inset-top),12px)]">
          <div className="go-fade-up flex items-center gap-4 rounded-[1.5rem] bg-[#1E4DB7] p-4 text-white" style={{ boxShadow: "0 12px 40px rgba(30,77,183,0.4)" }}>
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/15">
              <CurIcon size={30} strokeWidth={2.5} color="#fff" />
            </div>
            <div className="min-w-0 flex-1">
              {distToTurn != null && (
                <div className="font-head text-lg font-extrabold">{distToTurn < 1000 ? `${Math.round(distToTurn)} m` : `${(distToTurn / 1000).toFixed(1)} km`}</div>
              )}
              <div className="truncate text-sm text-white/90" data-testid="nav-current-step">{cur.instruction}</div>
            </div>
            <button onClick={stopNav} className="shrink-0 rounded-full bg-white/15 p-2 active:scale-90" data-testid="nav-end">
              <X size={20} color="#fff" />
            </button>
          </div>
          {next && (
            <div className="mx-2 mt-1 flex items-center gap-2 rounded-b-2xl bg-[#0B1730]/90 ring-1 ring-white/10 px-4 py-2 text-xs font-semibold text-[#8194B2] backdrop-blur">
              Then <span className="truncate text-[#C2D0E8]">{next.instruction}</span>
            </div>
          )}
        </div>
      )}

      {/* Bottom sheet */}
      {sel && (
        <div className="go-sheet-up absolute bottom-0 left-0 right-0 z-[900] rounded-t-[2rem] bg-[#0B1730] ring-1 ring-white/10 pb-[max(env(safe-area-inset-bottom),84px)] pt-3" style={{ boxShadow: "0 -12px 40px rgba(0,0,0,0.5)" }} data-testid="nav-route-sheet">
          <div className="mx-auto mb-2 h-1.5 w-12 rounded-full bg-white/15" />
          <div className="px-5">
            {navMode ? (
              <div className="flex items-center justify-between py-1">
                <div>
                  <div className="font-head text-2xl font-extrabold text-white" data-testid="nav-live-eta">{sel.duration}</div>
                  <div className="text-sm text-[#8194B2]">{sel.distance} · step {stepIdx + 1} of {steps.length}</div>
                </div>
                <button onClick={stopNav} className="rounded-full bg-slate-100 px-5 py-2.5 text-sm font-bold text-[#C2D0E8] active:scale-95">End</button>
              </div>
            ) : (
              <>
                <div className="mb-2 flex gap-2 overflow-x-auto go-scroll">
                  {data.routes.map((r, i) => {
                    const rk = RISK[r.risk_level] || RISK.medium;
                    const on = i === selIdx;
                    const best = i === data.recommended_index;
                    return (
                      <button key={i} onClick={() => setSelIdx(i)}
                        className={`min-w-[130px] rounded-2xl border-2 p-3 text-left transition-all active:scale-95 ${on ? "border-[#1E4DB7]" : "border-white/10"}`}
                        data-testid={`nav-route-option-${i}`}>
                        <div className="flex items-center gap-1.5">
                          <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: rk.bg, color: rk.c }}>{rk.label}</span>
                          {best && <span className="rounded-full bg-[#FFC726] px-1.5 py-0.5 text-[10px] font-bold text-white">Best</span>}
                        </div>
                        <div className="font-head mt-1.5 text-lg font-bold text-white">{r.duration}</div>
                        <div className="text-xs text-[#8194B2]">{r.distance}{r.summary ? ` · ${r.summary}` : ""}</div>
                      </button>
                    );
                  })}
                </div>

                {sel.warnings?.length > 0 && (
                  <div className="mb-2 rounded-2xl bg-[#FFC726]/12 px-4 py-2.5" data-testid="nav-warnings">
                    <div className="text-xs font-bold text-[#FFD54A]">Along this route</div>
                    <ul className="mt-1 space-y-0.5">
                      {sel.warnings.map((w, i) => (
                        <li key={i} className="text-xs text-[#FFE08A]">• {w}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <button onClick={() => setShowSteps((s) => !s)} className="flex w-full items-center justify-between py-1 text-sm font-semibold text-[#1E4DB7]" data-testid="nav-toggle-steps">
                  {showSteps ? "Hide directions" : `View ${steps.length} directions`}
                  <ChevronUp size={16} className={`transition-transform ${showSteps ? "" : "rotate-180"}`} />
                </button>
                {showSteps && (
                  <div className="go-scroll max-h-48 overflow-y-auto pb-2">
                    {steps.map((s, i) => {
                      const Ic = maneuverIcon(s.maneuver);
                      return (
                        <div key={i} className="flex items-start gap-3 border-b border-slate-50 py-2.5">
                          <Ic size={18} color="#1E4DB7" strokeWidth={2.3} className="mt-0.5 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm text-white">{s.instruction}</div>
                            <div className="text-xs text-[#67799A]">{s.distance} · {s.duration}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <button onClick={startNav}
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-[#10B981] py-3.5 text-[15px] font-bold text-white transition-transform active:scale-[0.98]"
                  data-testid="nav-start">
                  <Play size={18} strokeWidth={2.6} fill="#fff" /> Start navigation
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {!sel && !navMode && (
        <div className="absolute bottom-28 left-1/2 -translate-x-1/2 rounded-full bg-[#0B1730]/90 ring-1 ring-white/10 px-5 py-2.5 text-sm font-medium text-[#8194B2] backdrop-blur" style={{ boxShadow: "0 6px 20px rgba(0,0,0,0.08)" }}>
          Enter a destination to see the safest route
        </div>
      )}
    </div>
  );
};

export default NavigateScreen;
